import { friendlyLabel } from '../../utils/labels';
import HazardEditor from '../HazardEditor';
import LibraryMeasurements from '../LibraryMeasurements';
import { riskLibraryService } from '../../services/riskLibrary';
import { useAuth } from '../../contexts/AuthContext';
import { Hazard } from '../../types';
import { formatDate } from '../../utils/formatDate';
import React, { useState, useEffect } from 'react';
import {
  X,
  Save,
  Edit3,
  MapPin,
  Package,
  FileText,
  Paperclip,
  Calendar,
  AlertCircle,
  Info,
  AlertTriangle
} from 'lucide-react';
import { Asset, AssetTypeDef, AssetTypeInfo, Location } from '../../types';
import { AssetFile, assetsService, assetTypeRegistryService, locationsService } from '../../services';
import LocationSelector from '../LocationSelector';
import CreateLocationInline from '../CreateLocationInline';
import { getLocationPath } from '../../utils/locationPaths';
import PhotoCapture from '../PhotoCapture';
import FileAttachment from '../FileAttachment';
import RiskMatrix from '../RiskMatrix';
import './AssetDetailsModal.css';
import { FieldGroupRenderer } from './FieldRenderer';

interface AssetDetailsModalProps {
  initialCheckId?: string;
  isOpen: boolean;
  onClose: () => void;
  asset: Asset | null;
  onUpdated: (asset: Asset) => void;
  parentAsset?: Asset | null;
}

const AssetDetailsModal: React.FC<AssetDetailsModalProps> = ({
  isOpen,
  onClose,
  asset,
  onUpdated,
  parentAsset,
  initialCheckId
}) => {
  const { isConsultant, isH2okayAdmin } = useAuth();
  const [hazards, setHazards] = useState<Hazard[]>([]);
  const [riskOverride, setRiskOverride] = useState(false);
  const [libraryVersion, setLibraryVersion] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [assetTypeId, setAssetTypeId] = useState<string | null>(null);
  const [typeChoices, setTypeChoices] = useState<AssetTypeDef[]>([]);
  const [locationId, setLocationId] = useState<string>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [locationVersion, setLocationVersion] = useState(0);
  const [notes, setNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<Array<AssetFile & { url: string }>>([]);
  const [fileInputVersion, setFileInputVersion] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeInfo, setTypeInfo] = useState<AssetTypeInfo | null>(null);
  const [typeInfoStatus, setTypeInfoStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [riskLikelihood, setRiskLikelihood] = useState<number | undefined>();
  const [riskSignificance, setRiskSignificance] = useState<number | undefined>();
  const [isEditingRisk, setIsEditingRisk] = useState(false);
  const [registryType, setRegistryType] = useState<AssetTypeDef | null>(null);
  const [attributes, setAttributes] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (asset) {
      setHazards(asset.hazards || []);
      setRiskOverride(asset.risk_override || false);
      setName(asset.name);
      setType(asset.type);
      setAssetTypeId(asset.asset_type_id || null);
      setLocationId(asset.location_id);
      setNotes(asset.description || '');
      setRiskLikelihood(asset.assessed_risk_likelihood !== undefined ? asset.assessed_risk_likelihood ?? undefined : asset.risk_likelihood);
      setRiskSignificance(asset.assessed_risk_significance !== undefined ? asset.assessed_risk_significance ?? undefined : asset.risk_significance);
      setIsEditing(false);
      setIsEditingNotes(false);
      setIsEditingRisk(false);
      setError(null);
      setAttributes(asset.attributes || {});
    }
  }, [asset]);

  useEffect(() => {
    let cancelled = false;
    if (!isOpen || !assetTypeId) {
      setRegistryType(null);
      return;
    }
    assetTypeRegistryService.get(assetTypeId).then((row) => {
      if (!cancelled) setRegistryType(row);
    }).catch((err) => {
      console.warn('Asset type definition unavailable', err);
      if (!cancelled) setRegistryType(null);
    });
    return () => { cancelled = true; };
  }, [isOpen, assetTypeId]);

  useEffect(() => {
    if (!isEditing) return;
    let cancelled = false;
    assetTypeRegistryService.list(true, true).then(rows => {
      if (!cancelled) setTypeChoices(rows);
    }).catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load asset types'); });
    return () => { cancelled = true; };
  }, [isEditing]);

  useEffect(() => {
    setTypeInfo(registryType ? { ...registryType.metadata, name: registryType.name } as AssetTypeInfo : null);
    setTypeInfoStatus('ready');
  }, [registryType]);

  useEffect(() => {
    if (!isOpen || !asset) return;
    let cancelled = false;
    locationsService.getFacilityLocations(asset.facility_id).then(rows => {
      if (!cancelled) setLocations(rows);
    }).catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load locations'); });
    return () => { cancelled = true; };
  }, [isOpen, asset?.facility_id, locationVersion]);

  useEffect(() => {
    let cancelled = false;
    const objectUrls: string[] = [];

    const loadFiles = async () => {
      if (!isOpen || !asset) {
        setExistingFiles([]);
        return;
      }
      try {
        const metadata = await assetsService.listAssetFiles(asset.id);
        const loaded = await Promise.all(
          metadata.map(async file => {
            const blob = await assetsService.downloadAssetFile(asset.id, file.id);
            const url = URL.createObjectURL(blob);
            objectUrls.push(url);
            return { ...file, url };
          })
        );
        if (!cancelled) setExistingFiles(loaded);
      } catch (err) {
        console.error('Failed to load asset files', err);
        if (!cancelled) setError('Failed to load photos and attachments.');
      }
    };

    loadFiles();
    return () => {
      cancelled = true;
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [isOpen, asset]);

  if (!isOpen || !asset) return null;

  const handleSave = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const updated = await assetsService.updateAsset(asset.id, {
        facility_id: asset.facility_id,
        parent_asset_id: asset.parent_asset_id,
        name,
        type,
        asset_type_id: assetTypeId,
        ...(assetTypeId ? { attributes } : {}),
        location_id: locationId || null,
        description: notes,
        hazards,
        risk_override: riskOverride,
        risk_likelihood: riskLikelihood,
        risk_significance: riskSignificance,
      });

      await Promise.all([
        ...photos.map(file => assetsService.uploadAssetFile(asset.id, file, 'photo')),
        ...attachments.map(file => assetsService.uploadAssetFile(asset.id, file, 'attachment')),
      ]);

      if (photos.length > 0 || attachments.length > 0) {
        const metadata = await assetsService.listAssetFiles(asset.id);
        const loaded = await Promise.all(
          metadata.map(async file => ({
            ...file,
            url: URL.createObjectURL(
              await assetsService.downloadAssetFile(asset.id, file.id)
            ),
          }))
        );
        existingFiles.forEach(file => URL.revokeObjectURL(file.url));
        setExistingFiles(loaded);
        setPhotos([]);
        setAttachments([]);
        setFileInputVersion(version => version + 1);
      }

      onUpdated(updated);
      setIsEditing(false);
      setIsEditingNotes(false);
      setIsEditingRisk(false);
    } catch (e) {
      console.error('Update failed', e);
      setError(e instanceof Error ? e.message : 'Failed to update asset. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteFile = async (file: AssetFile & { url: string }) => {
    try {
      await assetsService.deleteAssetFile(asset.id, file.id);
      URL.revokeObjectURL(file.url);
      setExistingFiles(current => current.filter(item => item.id !== file.id));
    } catch (err) {
      console.error('Failed to delete asset file', err);
      setError('Failed to delete the file. Please try again.');
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setName(asset.name);
    setType(asset.type);
      setAssetTypeId(asset.asset_type_id || null);
    setLocationId(asset.location_id);
    setNotes(asset.description || '');
    setRiskLikelihood(asset.assessed_risk_likelihood !== undefined ? asset.assessed_risk_likelihood ?? undefined : asset.risk_likelihood);
    setRiskSignificance(asset.assessed_risk_significance !== undefined ? asset.assessed_risk_significance ?? undefined : asset.risk_significance);
    setAttributes(asset.attributes || {});
    setIsEditing(false);
    setIsEditingNotes(false);
    setIsEditingRisk(false);
    setError(null);
  };

  const insightList = (label: string, items?: string[]) => {
    if (!items || items.length === 0) {
      return null;
    }
    return (
      <div className="insight-block">
        <h4>{label}</h4>
        <ul>
          {items.map((item, idx) => (
            <li key={`${label}-${idx}`}>{item}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content asset-details-modal">
        <div className="modal-header">
          <div className="header-title-section">
            <div className="asset-icon-large">
              <Package size={28} />
            </div>
            <div>
              <h2>{name}</h2>
              <p className="asset-type-subtitle">{registryType?.name || friendlyLabel(type)}</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body asset-details-body">
          {error && (
            <div className="error-banner">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Key Info Section */}
          <div className="details-section key-info-section">
            <div className="section-header">
              <div className="section-title">
                <Info size={18} />
                <h3>Key Information</h3>
              </div>
              {!isEditing && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit3 size={16} />
                  Edit
                </button>
              )}
            </div>

            <div className="key-info-grid">
              <div className="info-item">
                <label className="info-label">Asset Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="info-input"
                  />
                ) : (
                  <div className="info-value">{name}</div>
                )}
              </div>

              <div className="info-item">
                <label className="info-label">Type</label>
                {isEditing ? <select aria-label="Asset type" value={assetTypeId || ''} onChange={event => {
                  const selected = typeChoices.find(row => row.id === event.target.value);
                  setAssetTypeId(selected?.id || null); setType(selected?.code || 'equipment'); setAttributes({});
                }}>
                  <option value="">Other/untyped</option>
                  {registryType && !typeChoices.some(row => row.id === registryType.id) && <option value={registryType.id}>{registryType.name}</option>}
                  {typeChoices.filter(row => row.is_active || row.id === assetTypeId).map(row => <option key={row.id} value={row.id}>{row.name}{!row.is_active ? ' (inactive)' : ''}</option>)}
                </select> : <div className="info-value">{registryType?.name || asset.type_name || friendlyLabel(type)}</div>}
              </div>

              <div className="info-item full-width">
                <label className="info-label">
                  <MapPin size={14} />
                  Location
                </label>
                {isEditing ? (
                  <>
                    <LocationSelector facilityId={asset.facility_id} value={locationId} onChange={setLocationId} refreshKey={locationVersion} />
                    <CreateLocationInline facilityId={asset.facility_id} locations={locations} onCreated={created => {
                      setLocationId(created.id); setLocations(current => [...current, created]); setLocationVersion(current => current + 1);
                    }} />
                  </>
                ) : (
                  <div className="info-value">{locations.find(item => item.id === locationId) ? getLocationPath(locations.find(item => item.id === locationId)!, locations) : 'No location'}</div>
                )}
                {asset.location && <p><strong>Notes on location:</strong> {asset.location}</p>}
              </div>

              <div className="info-item">
                <label className="info-label">
                  <Calendar size={14} />
                  Created
                </label>
                <div className="info-value">
                  {formatDate(asset.created_at)}
                </div>
              </div>

              <div className="info-item">
                <label className="info-label">
                  <Calendar size={14} />
                  Last Updated
                </label>
                <div className="info-value">
                  {formatDate(asset.updated_at)}
                </div>
              </div>

              {attributes._archived && typeof attributes._archived === 'object' ? (
                <section>
                  <h4>Fields no longer in this type</h4>
                  {Object.entries(attributes._archived).map(([key, value]) => (
                    <p key={key}>{key}: {String(value)}</p>
                  ))}
                </section>
              ) : null}
              {registryType && registryType.field_schema.length > 0 && (
                <div className="info-item full-width asset-attributes">
                  {isEditing ? (
                    <FieldGroupRenderer field_schema={registryType.field_schema} attributes={attributes} onChange={(key, value) => setAttributes((current) => ({ ...current, [key]: value }))} />
                  ) : (
                    <dl>
                      {registryType.field_schema.map((field) => {
                        const value = attributes[field.key];
                        const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value;
                        return (
                          <React.Fragment key={field.key}>
                            <dt>{field.label}</dt>
                            <dd>{displayValue === undefined || displayValue === null || displayValue === '' ? '—' : String(displayValue)}{displayValue !== undefined && displayValue !== null && displayValue !== '' && (field.unit || field.type === 'temperature' || field.type === 'percentage') ? ` ${field.unit || (field.type === 'temperature' ? '°C' : '%')}` : ''}</dd>
                          </React.Fragment>
                        );
                      })}
                    </dl>
                  )}
                </div>
              )}
            </div>

            <div className="type-insights">
              <div className="type-insights-header">
                <Info size={18} />
                <div>
                  <h4>{typeInfo?.name || 'Asset Type Guidance'}</h4>
                  <p>Preloaded best-practice notes for this asset type</p>
                </div>
              </div>
              {typeInfoStatus === 'loading' && (
                <p className="insight-hint">Loading key information…</p>
              )}
              {typeInfoStatus === 'error' && (
                <p className="insight-error">
                  We couldn’t load insights for this asset type right now.
                </p>
              )}
              {typeInfoStatus !== 'loading' && !typeInfo && (
                <p className="insight-hint">
                  No catalog metadata is available for this asset type yet.
                </p>
              )}
              {typeInfo && typeInfoStatus !== 'error' && (
                <div className="type-insights-grid">
                  {typeInfo.manufacturer && (
                    <div className="insight-block">
                      <h4>Manufacturer</h4>
                      <p>{typeInfo.manufacturer}</p>
                    </div>
                  )}
                  {typeInfo.legal_requirements && (
                    <div className="insight-block">
                      <h4>Legal Requirements</h4>
                      <p>{typeInfo.legal_requirements}</p>
                    </div>
                  )}
                  {typeInfo.replacement_schedule && (
                    <div className="insight-block">
                      <h4>Replacement Schedule</h4>
                      <p>{typeInfo.replacement_schedule}</p>
                    </div>
                  )}
                  {insightList('Common Gotchas', typeInfo.gotchas)}
                  {insightList('Red Flags', typeInfo.red_flags)}
                  {insightList('Maintenance Tips', typeInfo.maintenance_tips)}
                  {insightList('Key Metrics', typeInfo.key_metrics)}
                </div>
              )}
            </div>

            {isEditing && (
              <div className="edit-actions">
                <button
                  className="btn btn-ghost"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={submitting}
                >
                  <Save size={16} />
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div className="details-section notes-section">
            <div className="section-header">
              <div className="section-title">
                <FileText size={18} />
                <h3>Notes & Description</h3>
              </div>
              {!isEditingNotes && !isEditing && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setIsEditingNotes(true)}
                >
                  <Edit3 size={16} />
                  Edit Notes
                </button>
              )}
            </div>

            {isEditingNotes ? (
              <div className="notes-edit-container">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="notes-textarea"
                  rows={6}
                  placeholder="Add notes, observations, or maintenance history..."
                />
                <div className="edit-actions">
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setNotes(asset.description || '');
                      setIsEditingNotes(false);
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={submitting}
                  >
                    <Save size={16} />
                    {submitting ? 'Saving...' : 'Save Notes'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="notes-display">
                {notes ? (
                  <p>{notes}</p>
                ) : (
                  <p className="notes-empty">No notes added yet. Click "Edit Notes" to add information.</p>
                )}
              </div>
            )}
          </div>

          <HazardEditor value={hazards} onChange={setHazards} readOnly={!isConsultant && !isH2okayAdmin}/>
          {(isConsultant || isH2okayAdmin) && <>
            <label><input type="checkbox" checked={riskOverride} onChange={e=>setRiskOverride(e.target.checked)}/>Manually override overall asset risk</label>
            <button disabled={submitting} onClick={handleSave}>Save hazards</button>
            <button disabled={submitting} onClick={async()=>{setSubmitting(true);try{await riskLibraryService.applyAsset(asset.id); const updated=await assetsService.getAssetById(asset.id);setHazards(updated.hazards || []);onUpdated?.(updated);setLibraryVersion(v=>v+1);}catch(e){setError(e instanceof Error?e.message:'Unable to apply library');}finally{setSubmitting(false);}}}>Apply library checks</button>
            <LibraryMeasurements key={libraryVersion} assetId={asset.id} initialCheckId={initialCheckId}/>
          </>}
          {/* Risk Assessment Section */}
          <div className="details-section risk-section">
            <div className="section-header">
              <div className="section-title">
                <AlertTriangle size={18} />
                <h3>Risk Assessment</h3>
              </div>
              {!isEditingRisk && !isEditing && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setIsEditingRisk(true)}
                >
                  <Edit3 size={16} />
                  Edit Risk
                </button>
              )}
            </div>

            {isEditingRisk ? (
              <div className="risk-edit-container">
                <RiskMatrix
                  likelihood={riskLikelihood}
                  significance={riskSignificance}
                  onSelect={(l, s) => {
                    setRiskOverride(true);
                    setRiskLikelihood(l);
                    setRiskSignificance(s);
                  }}
                  size="medium"
                />
                <div className="edit-actions">
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setRiskLikelihood(asset.assessed_risk_likelihood !== undefined ? asset.assessed_risk_likelihood ?? undefined : asset.risk_likelihood);
                      setRiskSignificance(asset.assessed_risk_significance !== undefined ? asset.assessed_risk_significance ?? undefined : asset.risk_significance);
                      setIsEditingRisk(false);
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={submitting}
                  >
                    <Save size={16} />
                    {submitting ? 'Saving...' : 'Save Risk Assessment'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="risk-display">
                {riskLikelihood && riskSignificance ? (
                  <RiskMatrix
                    likelihood={riskLikelihood}
                    significance={riskSignificance}
                    readonly
                    size="medium"
                  />
                ) : (
                  <div className="risk-empty">
                    <AlertTriangle size={24} />
                    <p>No risk assessment performed yet.</p>
                    <p className="empty-hint">Click "Edit Risk" to assess this asset's risk level.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Photos & Files Section */}
          <div className="details-section files-section">
            <div className="section-header">
              <div className="section-title">
                <Paperclip size={18} />
                <h3>Photos & Attachments</h3>
              </div>
            </div>

            <PhotoCapture
              key={`photos-${fileInputVersion}`}
              onPhotosChange={setPhotos}
              maxPhotos={10}
              existingPhotos={existingFiles.filter(file => file.kind === 'photo').map(file => file.url)}
            />

            <div style={{ marginTop: '16px' }}>
              <FileAttachment
                key={`attachments-${fileInputVersion}`}
                onFilesChange={setAttachments}
                maxFiles={10}
                acceptedTypes=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv"
                existingFiles={[]}
              />
            </div>

            {existingFiles.length > 0 && (
              <div className="file-list" style={{ marginTop: '16px' }}>
                {existingFiles.map(file => (
                  <div className="file-item" key={file.id}>
                    <FileText size={20} />
                    <div className="file-info">
                      <a href={file.url} download={file.filename} className="file-name">
                        {file.filename}
                      </a>
                      <div className="file-label">
                        {file.kind === 'photo' ? 'Photo' : 'Attachment'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="file-remove"
                      onClick={() => handleDeleteFile(file)}
                      aria-label={`Delete ${file.filename}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {(photos.length > 0 || attachments.length > 0) && (
              <div className="photos-note">
                <p>📝 Files will be uploaded when you save changes to this asset.</p>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <div className="footer-info">
            <span className="asset-id">Asset ID: {asset.id.slice(0, 8)}...</span>
          </div>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetDetailsModal;
